import React from 'react';

// ─── Constants ──────────────────────────────────────────────────────────────

const STAT_LABELS = {
  pass_yds:       'Passing yards (pts/yd)',
  pass_td:        'Passing TD',
  pass_int:       'Interception thrown',
  pass_2pt:       'Passing 2pt conversion',
  rush_yds:       'Rushing yards (pts/yd)',
  rush_td:        'Rushing TD',
  rush_2pt:       'Rushing 2pt conversion',
  rec:            'Reception',
  rec_yds:        'Receiving yards (pts/yd)',
  rec_td:         'Receiving TD',
  rec_2pt:        'Receiving 2pt conversion',
  fumble_lost:    'Fumble lost',
  return_td:      'Return TD',
  fg_0_39:        'FG made 0–39 yd',
  fg_40_49:       'FG made 40–49 yd',
  fg_50_plus:     'FG made 50+ yd',
  pat:            'PAT made',
  fg_miss:        'FG missed',
  def_sack:       'Sack',
  def_int:        'Defensive INT',
  def_fumble_rec: 'Fumble recovered',
  def_td:         'Defensive / ST TD',
  def_safety:     'Safety',
  def_pts_0:      'Points allowed: shutout',
  def_pts_1_6:    'Points allowed: 1–6',
  def_pts_7_13:   'Points allowed: 7–13',
  def_pts_14_20:  'Points allowed: 14–20',
  def_pts_21_27:  'Points allowed: 21–27',
  def_pts_28_34:  'Points allowed: 28–34',
  def_pts_35_plus:'Points allowed: 35+',
};

const STANDARD_BASE = {
  pass_yds: 0.04, pass_td: 4, pass_int: -2, pass_2pt: 2,
  rush_yds: 0.1,  rush_td: 6, rush_2pt: 2,
  rec_yds: 0.1,   rec_td: 6,  rec_2pt: 2,
  fumble_lost: -2, return_td: 6,
  fg_0_39: 3, fg_40_49: 4, fg_50_plus: 5, pat: 1, fg_miss: -1,
  def_sack: 1, def_int: 2, def_fumble_rec: 2, def_td: 6, def_safety: 2,
  def_pts_0: 10, def_pts_1_6: 7, def_pts_7_13: 4, def_pts_14_20: 1,
  def_pts_21_27: 0, def_pts_28_34: -1, def_pts_35_plus: -4,
};

export const PRESETS = {
  standard: { ...STANDARD_BASE, rec: 0 },
  half_ppr: { ...STANDARD_BASE, rec: 0.5 },
  ppr:      { ...STANDARD_BASE, rec: 1.0 },
};

const PASSING_KEYS   = ['pass_yds', 'pass_td', 'pass_int', 'pass_2pt'];
const RUSHING_KEYS   = ['rush_yds', 'rush_td', 'rush_2pt'];
const RECEIVING_KEYS = ['rec', 'rec_yds', 'rec_td', 'rec_2pt'];
const MISC_KEYS      = ['fumble_lost', 'return_td'];
const KICKING_KEYS   = ['fg_0_39', 'fg_40_49', 'fg_50_plus', 'pat', 'fg_miss'];
const DEFENSE_KEYS   = [
  'def_sack', 'def_int', 'def_fumble_rec', 'def_td', 'def_safety',
  'def_pts_0', 'def_pts_1_6', 'def_pts_7_13', 'def_pts_14_20',
  'def_pts_21_27', 'def_pts_28_34', 'def_pts_35_plus',
];

const YARDAGE_KEYS = new Set(['pass_yds', 'rush_yds', 'rec_yds']);

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Returns 'standard' | 'half_ppr' | 'ppr' | 'custom' based on the rec value. */
export function derivedPreset(rules) {
  const rec = rules?.rec ?? 0;
  if (rec === 0)   return 'standard';
  if (rec === 0.5) return 'half_ppr';
  if (rec === 1.0) return 'ppr';
  return 'custom';
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function ScoringSection({ title, keys, rules, onChange, readOnly }) {
  return (
    <div className='scoring-section'>
      <h4 className='scoring-section-title'>{title}</h4>
      {keys.map(key => (
        <div key={key} className='scoring-row'>
          <span className='scoring-label'>{STAT_LABELS[key] ?? key}</span>
          {readOnly ? (
            <span className={`scoring-value${(rules[key] ?? 0) < 0 ? ' scoring-value--negative' : ''}`}>
              {rules[key] ?? 0}
            </span>
          ) : (
            <input
              type='number'
              className='scoring-input'
              value={rules[key] ?? 0}
              step={YARDAGE_KEYS.has(key) ? 0.01 : 0.5}
              onChange={e => onChange({ ...rules, [key]: parseFloat(e.target.value) || 0 })}
            />
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

/**
 * Displays and optionally edits per-stat scoring rules for a league.
 *
 * @param {Object}   rules    - Map of stat key → point value
 * @param {Function} onChange - Called with updated rules map on any change
 * @param {boolean}  readOnly - When true, renders plain text values instead of inputs
 */
export default function ScoringRulesEditor({ rules = {}, onChange, readOnly = false }) {
  const preset = derivedPreset(rules);

  const presetLabels = [
    { key: 'standard', label: 'Standard' },
    { key: 'half_ppr', label: '0.5 PPR' },
    { key: 'ppr',      label: 'PPR' },
  ];

  return (
    <div className='scoring-rules-editor'>
      {!readOnly && (
        <div className='scoring-preset-row'>
          <span className='scoring-preset-label'>Preset:</span>
          {presetLabels.map(p => (
            <button
              type='button'
              key={p.key}
              className={`filter-pill${preset === p.key ? ' active' : ''}`}
              onClick={() => onChange(PRESETS[p.key])}
            >
              {p.label}
            </button>
          ))}
          {preset === 'custom' && (
            <span className='filter-pill active'>Custom</span>
          )}
        </div>
      )}

      <ScoringSection title='Passing'                keys={PASSING_KEYS}   rules={rules} onChange={onChange} readOnly={readOnly} />
      <ScoringSection title='Rushing'                keys={RUSHING_KEYS}   rules={rules} onChange={onChange} readOnly={readOnly} />
      <ScoringSection title='Receiving'              keys={RECEIVING_KEYS} rules={rules} onChange={onChange} readOnly={readOnly} />
      <ScoringSection title='Misc Offense'           keys={MISC_KEYS}      rules={rules} onChange={onChange} readOnly={readOnly} />
      <ScoringSection title='Kicking'                keys={KICKING_KEYS}   rules={rules} onChange={onChange} readOnly={readOnly} />
      <ScoringSection title='Defense / Special Teams' keys={DEFENSE_KEYS}  rules={rules} onChange={onChange} readOnly={readOnly} />
    </div>
  );
}
